import { Component, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
    OfflineStatusService,
    LoggedInUserService,
    ConfigService,
    select,
    WebSocketService,
    BroadcastService,
    ClientConstants
} from '@formbird/services';
import { OfflineStatus } from '@formbird/types';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'ft-offline-status',
    templateUrl: 'offline-status.component.html',
    styleUrls: ['./offline-status.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OfflineStatusComponent implements OnInit, OnDestroy {

    @select(['offlineReducer', 'offlineStatus']) offlineStatus$: Observable<OfflineStatus>;

    isMenuOpened = false;
    isOffline = false;
    cloudIcon = 'cloud_queue';
    
    offlineStatus: OfflineStatus;
    operateOnline: boolean;
    cacheEnabled: boolean;
    isCachingNotAllowed: boolean;

    // Cache progress status values
    appValue: number = 0;
    dataValue: number = 0;
    mapValue: number = 0;
    fileValue: number = 0;

    private subs: Subscription = new Subscription();

    constructor(
        private offlineStatusService: OfflineStatusService,
        private loggedInUserService: LoggedInUserService,
        private configService: ConfigService,
        private cd: ChangeDetectorRef,
        private webSocketService: WebSocketService,
        private broadcastService: BroadcastService
    ) {
        this.cacheEnabled = this.offlineStatusService.isCachingEnabled();
        const clientConfig = this.configService.clientConfig();
        this.isCachingNotAllowed = clientConfig?.cachingNotAllowed;
        this.operateOnline = this.offlineStatusService.shouldOperateOnline();
        this.isOffline = !this.operateOnline; // Sync local state
        this.postMessageToServiceWorker(this.operateOnline); 


        // #30685 - get counts from IndexedDB, caches, and update offlineStatus
        this.offlineStatusService.initializeOfflineCounts();
    }

    ngOnInit() {
        const subOfflineStatus = this.offlineStatusService.offlineStatus$.subscribe((offlineStatusData) => {
            this.updateStatus(offlineStatusData);
        });

        const subOfflineRedux = this.offlineStatus$.subscribe((offlineStatusData) => {
            this.updateStatus(offlineStatusData);
        });

        this.loggedInUserService.addUserConfigItemListener('cachingEnabled', newValue => {
            this.cacheEnabled = newValue;
            if (this.offlineStatus) {
                this.updateStatus(this.offlineStatus);
            }
        });

        this.subs.add(subOfflineStatus);
        this.subs.add(subOfflineRedux);
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    updateStatus(offlineStatusData: OfflineStatus) {
        // Add max value aliases if not present
        const status = { ...offlineStatusData } as any; // Clone to allow mutation

        // Preserve the highest observed dataMaxCount so it never decreases
        const previousDataMaxCount = (this.offlineStatus as any)?.dataMaxCount ?? 0;
        const incomingDataMaxCount = (offlineStatusData as any).dataMaxCount ?? (offlineStatusData as any).maxCacheCount ?? 0;
        const stableDataMaxCount = Math.max(previousDataMaxCount, incomingDataMaxCount);

        status.appMaxCount = status.appMaxCount ?? status.totalStaticResourceCount ?? 0;
        status.dataMaxCount = stableDataMaxCount;
        status.mapMaxCount = status.mapMaxCount ?? status.tileServerMaxCacheCount ?? 0;
        status.fileMaxCount = status.fileMaxCount ?? status.maxCacheAttachedFileCount ?? 0;

        this.offlineStatus = status;
        
        // Store current counts
        this.appValue = status.currentStaticResourceCount || 0;
        this.dataValue = status.currentCacheCount || 0;
        this.mapValue = status.tileServerCacheCount || 0;
        this.fileValue = status.currentCacheAttachedFileCount || 0;
        
        if (!offlineStatusData.connected) {
            this.cloudIcon = 'cloud_off';
        } else if (!this.cacheEnabled) {
            this.cloudIcon = 'cloud_queue';
        } else if (!this.offlineStatusService.shouldOperateOnline()) {
            this.cloudIcon = 'cloud_operate_offline';
        } else if (!status.connected) {
            this.cloudIcon = 'cloud_off';       
        } else if (status.pendingDocumentCount > 0 || (typeof status.cacheLoading === 'number' && status.cacheLoading > 0) || status.pendingUploadCount > 0) {
            this.cloudIcon = 'cloud_sync';
        } else {
            this.cloudIcon = 'cloud_done';
        }
        this.cd.detectChanges();
    }

    getDisplayCount(current: number, max: number): string {
        if (!max || max <= 0) return '0 / 0';
        const displayCurrent = current === 0 ? max : current;
        return `${displayCurrent} / ${max}`;
    }

    getStatusClass(current: number, max: number): string {
        if (!max || max <= 0) return 'high';
        
        // current === 0 means all loaded as per getDisplayCount
        if (current === 0 || current >= max) {
            return 'high';
        }

        const progress = (current / max) * 100;
        if (progress < 30) {
            return 'low';
        } else if (progress < 95) {
            return 'medium';
        } else {
            return 'high';
        }
    }

    menuOpened() {
        this.isMenuOpened = true;
    }

    menuClosed() {
        this.isMenuOpened = false;
    }

    toggleOffline(event: Event) {
        event.stopPropagation();
        this.operateOnline = !this.operateOnline;
        this.isOffline = !this.operateOnline;
        
        this.offlineStatusService.setOperateOnline(this.operateOnline);
        this.postMessageToServiceWorker(this.operateOnline);

        if (this.operateOnline) {
            this.webSocketService.start();
            this.broadcastService.broadcast(ClientConstants.CHECK_UNSYNCED_DOCUMENTS, {operateOnline: true});
        }
    }

    postMessageToServiceWorker(operateOnline) {
        navigator?.serviceWorker?.ready?.then((registration) => {
            registration.active.postMessage({ type: 'OPERATE_ONLINE', operateOnline });
        });
    }

    clearErrorCachingAttachedFiles() {
        this.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
        this.loggedInUserService.setUserConfigItem('ErrorCachingAttachedFiles', this.offlineStatusService.offlineStatus.errorCacheAttachedFile);
    }

    clearErrorOfflineChangesSyncDocs() {
        this.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs = [];
        this.loggedInUserService.setUserConfigItem('ErrorOfflineChangesSyncDocs', this.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs);
    }
}
